import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Modal, Spin, message } from "antd";
import { ArrowLeftOutlined, CreditCardOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getCardInfo, unlinkCard } from "./api_v2/subscription";
import { getToken } from "./api_v2/client";
import "./UnlinkCardPage.css";

const formatMaskedPan = (value) => {
  const raw = String(value || "").trim();
  const last4 = raw.match(/(\d{4})\D*$/)?.[1];
  return last4 ? `**** ${last4}` : raw;
};

const UnlinkCardPage = () => {
  const [loading, setLoading] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [cards, setCards] = useState([]);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const fetchCards = useCallback(async () => {
    if (!getToken()) {
      message.warning("Войдите в профиль, чтобы управлять привязанной картой.");
      navigate("/register");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await getCardInfo();
      if (!data.success) {
        throw new Error(data.message || "Ошибка получения данных карты.");
      }
      setCards(Array.isArray(data.cards) ? data.cards : []);
    } catch (err) {
      const text = err?.response?.data?.message || err?.message || "Ошибка загрузки данных карты.";
      setError(text);
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const doUnlinkCard = async () => {
    setUnlinking(true);
    try {
      const data = await unlinkCard();
      if (!data.success) {
        throw new Error(data.message || "Ошибка при отвязке карты.");
      }
      setCards([]);
      message.success("Карта отвязана. Автоплатежи по ней отключены.");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Не удалось отвязать карту.");
    } finally {
      setUnlinking(false);
    }
  };

  const handleUnlinkCard = () => {
    Modal.confirm({
      title: "Отвязать карту?",
      content:
        "После удаления карта исчезнет из личного кабинета, а автоплатежи и оплата в один клик по этой карте будут отключены.",
      okText: "Отвязать карту",
      cancelText: "Отмена",
      okButtonProps: { danger: true, loading: unlinking },
      onOk: doUnlinkCard,
    });
  };

  return (
    <div className="unlink-card-container">
      <Button
        type="text"
        className="unlink-card-back"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/profile")}
      >
        Назад
      </Button>

      <div className="unlink-card-header">
        <CreditCardOutlined />
        <div>
          <h1>Управление картой</h1>
          <p>Здесь можно удалить привязанную карту в любой момент.</p>
        </div>
      </div>

      {loading ? (
        <Spin tip="Загрузка карты...">
          <div className="unlink-card-loading">Загрузка информации о карте...</div>
        </Spin>
      ) : (
        <div className="unlink-card-content">
          {error && (
            <Alert
              type="error"
              showIcon
              message="Не удалось загрузить карту"
              description={error}
              action={
                <Button size="small" icon={<ReloadOutlined />} onClick={fetchCards}>
                  Повторить
                </Button>
              }
            />
          )}

          {!error && cards.length > 0 && (
            <>
              <div className="unlink-card-list">
                {cards.map((card, index) => (
                  <div className="unlink-card-item" key={`${card.masked_pan}-${index}`}>
                    <div className="unlink-card-item__icon">
                      <CreditCardOutlined />
                    </div>
                    <div className="unlink-card-item__body">
                      <span className="unlink-card-item__label">Привязанная карта</span>
                      <strong>
                        {card.card_type || "Карта"} {formatMaskedPan(card.masked_pan)}
                      </strong>
                      {card.expiry_date && <small>Срок действия: {card.expiry_date}</small>}
                    </div>
                  </div>
                ))}
              </div>

              <Button
                type="primary"
                danger
                size="large"
                icon={<DeleteOutlined />}
                loading={unlinking}
                onClick={handleUnlinkCard}
                block
              >
                Отвязать карту
              </Button>
            </>
          )}

          {!error && cards.length === 0 && (
            <div className="unlink-card-empty">
              <CreditCardOutlined />
              <h2>Карта не привязана</h2>
              <p>После удаления сохранённая карта больше не отображается в личном кабинете.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UnlinkCardPage;
